/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { CrossLocationPrerequisites, TeamsInTenant } from "@microsoft/shifts-models-data";
import { CrossLocationPrerequisitesInfo } from "@microsoft/shifts-models-view";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { DataService } from "sh-services/dataservice/DataService";

import { CrossLocationPrerequisitesDataProvider } from "./CrossLocationPrerequisitesDataProvider";
import { ITeamSettingsDataService } from "./ITeamSettingsDataService";
import { TeamsInTenantDataProvider } from "./TeamsInTenantDataProvider";

/**
 * The data service for cross-location teams.
 */
export class TeamSettingsDataService extends DataService implements ITeamSettingsDataService {
    /**
     * Gets the cross-location teams for the current user.
     * @param teamId The team id.
     * @param setDataInState The callback to set the view models data in state.
     * @returns The cross-location teams.
     */
    public async verifyCrossLocationPrerequisites(
        teamId: string,
        setDataInState: (value: CrossLocationPrerequisitesInfo) => void
    ): Promise<CrossLocationPrerequisitesInfo> {
        const setDataInMemory = (value: CrossLocationPrerequisites): void => {
            setDataInState(value);
        };

        const provider = new CrossLocationPrerequisitesDataProvider({
            setDataInMemory,
            teamId,
            tenantId: this.tenantId,
            userId: this.loggedInUserId
        });

        return await this.getData<CrossLocationPrerequisites>(provider);
    }

    /**
     * Get teams in tenants
     * @param setDataInState The callback to set the view models data in state.
     * @returns The teams in tenant
     */
    public async getCrossLocationTeamsInTenant(
        setDataInState: (value: TeamsInTenant) => void
    ): Promise<TeamsInTenant> {
        const setDataInMemory = (value: TeamsInTenant): void => {
            setDataInState(value);
        };

        const provider = new TeamsInTenantDataProvider({
            setDataInMemory
        });

        return await this.getData<TeamsInTenant>(provider);
    }

    /**
     * Resets the sync state.
     */
    public async resetSyncState(): Promise<void> {
        return Promise.resolve();
    }
}
